import React from "react";

import { Tooltip } from "@/components/Tooltip/Tooltip";
import {
	hasLeadScoringContent,
	LeadScoringPopover,
	LeadScoringSource,
} from "@/components/LeadScoringPopover/LeadScoringPopover";

/**
 * Renders the round, colour-coded P-score pill plus its hover.
 *
 * When `scoring` is supplied the hover is the beautified structured Lead Scoring
 * card (PREC-3499) — sticky header + prio badge, scrollable Company/Persona
 * criteria, sticky overall-score footer — falling back to the legacy
 * `overallReasoning` HTML *inside the same frame* when no structured breakdown
 * is available. Without `scoring` (company-only branches) it keeps the legacy
 * raw-reasoning tooltip driven by `reason`.
 *
 * `scoring` is typed structurally, so a caller whose reasoning lives somewhere
 * other than a persona result (a cascading company verdict) can hand the card a
 * `{ overallReasoning }` of its own rather than falling out of it.
 */
export const renderCurrentScoreResult = ({
	reason,
	score,
	scoring,
	note,
	triggerRef,
	setIsVisible,
	isVisible,
}: {
	reason?: string | null;
	score: string;
	scoring?: LeadScoringSource | null;
	/** Score-level caveat for the card header — "company information only" for a C tier. */
	note?: string;
	triggerRef?: React.RefObject<HTMLDivElement>;
	setIsVisible?: (isVisible: boolean) => void;
	isVisible?: boolean;
}) => {
	let className =
		"cursor-pointer w-[24px] h-[24px] text-white text-xxs  font-bold bg-error rounded-full justify-center items-center flex";

	if (score === "?") {
		className =
			"cursor-pointer w-[24px] h-[24px] text-text-meta text-xs  font-bold bg-border-light rounded-full justify-center items-center flex";
	} else if (score === "DQ") {
		className =
			"cursor-pointer w-[24px] h-[24px] text-white text-xxs  font-bold bg-error rounded-full justify-center items-center flex";
	} else if (score === "P1" || score === "C1") {
		className =
			"cursor-pointer flex w-[24px] h-[24px] text-xxs text-text-success border border-text-success font-bold bg-background-success rounded-full justify-center items-center";
	} else if (score === "P2" || score === "C2") {
		className =
			"cursor-pointer flex w-[24px] h-[24px] text-xxs text-text-warning border border-text-warning font-bold bg-background-warning rounded-full justify-center items-center";
	} else if (score === "P3" || score === "C3") {
		className =
			"cursor-pointer w-[24px] h-[24px] border border-error text-error text-xxs  font-bold bg-error-light rounded-full justify-center items-center flex";
	}

	const renderReasoning = () => {
		const reasoning = reason ?? "";

		// eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml
		return <div dangerouslySetInnerHTML={{ __html: reasoning }} />;
	};

	const content = scoring ? (
		<LeadScoringPopover note={note} scoreLabel={score} scoring={scoring} />
	) : (
		renderReasoning()
	);

	const disabled = scoring ? !hasLeadScoringContent(scoring) : !reason;

	return (
		<Tooltip content={content} delay={300} disabled={disabled} panel={!!scoring}>
			<div
				ref={triggerRef}
				className={className}
				onClick={(e) => {
					e.preventDefault();
					e.stopPropagation();
					setIsVisible?.(!isVisible);
				}}
			>
				{score}
			</div>
		</Tooltip>
	);
};
